import { createEnquirySession, type CreateEnquirySessionPayload } from 'api/accommodationBooking'
import { withBaseSessionPricing } from 'lib/accommodation/buildEnquirySessionPayload'
import { reportClientMessage } from 'services/errorReportingService'

export interface AccommodationV2SessionCreateResult {
  session: App.AccommodationEnquirySession
  /** Set when the claim was dropped: the un-marked pricing the session was created at instead. */
  fallbackPricing?: CreateEnquirySessionPayload['pricing']
}

/**
 * Why svc-accommodation would not price the claim: its `CHANNEL_MARKUP_*` refusal code, or the
 * status of a 5xx it failed with while checking it (as when svc-promo is down).
 */
function getChannelMarkupClaimFailure(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('status' in error)) return undefined
  const { status, errors } = error as { status?: unknown, errors?: Array<{ code?: unknown }> }
  if (typeof status === 'number' && status >= 500) return String(status)
  if (status !== 422) return undefined
  const code = errors?.find(entry => typeof entry?.code === 'string' && entry.code.startsWith('CHANNEL_MARKUP_'))?.code
  return typeof code === 'string' ? code : undefined
}

/**
 * Creates the session, and once more without its channel markup claim, at the claim's un-marked
 * base prices, if svc-accommodation will not price the claim. A refreshed rate is mapped with the
 * same markup source, so retrying the claim would fail again; the session instead takes the
 * price the customer can be charged, and checkout enquiry shows it as a price change.
 *
 * Every fallback is reported with its reason, so claims failing across the board (contract drift,
 * an svc-promo outage) show up rather than quietly dropping markup revenue to zero. A caller
 * holding a cart item applies `fallbackPricing` to it (see `withoutDroppedChannelMarkupClaim`),
 * so the item stops carrying a claim that will only fail again.
 */
export async function createEnquirySessionWithMarkupFallback(
  payload: CreateEnquirySessionPayload,
): Promise<AccommodationV2SessionCreateResult> {
  try {
    return { session: await createEnquirySession(payload) }
  } catch(error) {
    const { channelMarkup, ...unclaimed } = payload
    const failure = channelMarkup ? getChannelMarkupClaimFailure(error) : undefined
    if (!channelMarkup || !failure) throw error

    reportClientMessage(
      'AccommodationV2: channel markup claim dropped at session create',
      { channelMarkupId: channelMarkup.channelMarkupId, markupId: channelMarkup.markupId, failure },
      'warning',
      { channelMarkupClaimFailure: failure },
    )
    const fallbackPricing = withBaseSessionPricing(payload.pricing, channelMarkup)
    return { session: await createEnquirySession({ ...unclaimed, pricing: fallbackPricing }), fallbackPricing }
  }
}

type PropertyRatePricing = App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing']

/**
 * One side of a rate at its un-marked selling price. `getAccommodationV2ItemView` charges an applied
 * upgrade from `sourcePricing.totals.sellingPrice` (and the member price from `luxPlusTotals`), so a
 * dropped claim has to move those off the marked price too. A side with no markup is left as it is.
 */
function unmarkedPropertyRateTotals(totals: App.PropertyRateTotals): App.PropertyRateTotals {
  if (!totals.channelMarkup) return totals
  const { channelMarkup: _channelMarkup, ...unmarked } = totals
  return {
    ...unmarked,
    sellingPrice: totals.baseSellingPrice ?? totals.sellingPrice,
  }
}

function unmarkedPropertyRatePricing(pricing: PropertyRatePricing): PropertyRatePricing {
  return {
    totals: unmarkedPropertyRateTotals(pricing.totals),
    ...(pricing.luxPlusTotals && { luxPlusTotals: unmarkedPropertyRateTotals(pricing.luxPlusTotals) }),
  }
}

/**
 * A toggle restores this side's rate fields, claim and session included. Drop the claim here or the
 * next session sends the markup that was just refused, and drop the session, which was created at
 * the marked price: a toggle reuses a usable session, so keeping it would charge the marked price
 * again. The member price is kept only when the claim has a base for it, matching
 * `withBaseSessionPricing`.
 */
function unmarkedAccommodationV2RateFields(
  rate: App.Checkout.AccommodationV2RateFields,
): App.Checkout.AccommodationV2RateFields {
  if (!rate.channelMarkup) return rate
  const { channelMarkup, luxPlusTotal, sessionId: _markedSessionId, ...rest } = rate
  return {
    ...rest,
    sellTotal: channelMarkup.baseSellTotal,
    ...(luxPlusTotal != null && channelMarkup.baseLuxPlusTotal != null && {
      luxPlusTotal: channelMarkup.baseLuxPlusTotal,
    }),
  }
}

function withoutMarkedLoyaltyUpgradePricing(
  upgrade: App.Checkout.PropertyRateLoyaltyUpgrade,
): App.Checkout.PropertyRateLoyaltyUpgrade {
  return {
    ...upgrade,
    sourcePricing: unmarkedPropertyRatePricing(upgrade.sourcePricing),
    ...(upgrade.targetPricing && { targetPricing: unmarkedPropertyRatePricing(upgrade.targetPricing) }),
    ...(upgrade.sourceAccommodationV2Rate && {
      sourceAccommodationV2Rate: unmarkedAccommodationV2RateFields(upgrade.sourceAccommodationV2Rate),
    }),
    ...(upgrade.targetAccommodationV2Rate && {
      targetAccommodationV2Rate: unmarkedAccommodationV2RateFields(upgrade.targetAccommodationV2Rate),
    }),
  }
}

/**
 * A cart item at the pricing its session fell back to, without the claim that was refused. An applied
 * loyalty upgrade is charged from its source pricing, so that pricing — and the other side, which a
 * toggle would book — moves onto the un-marked base each side already carries. Without a fallback
 * the item is returned as it is.
 */
export function withoutDroppedChannelMarkupClaim<T extends App.Checkout.AccommodationV2Item>(
  item: T,
  fallbackPricing: CreateEnquirySessionPayload['pricing'] | undefined,
): T {
  if (!fallbackPricing) return item
  const { channelMarkup: _channelMarkup, luxPlusTotal: _luxPlusTotal, ...unclaimed } = item
  const upgrade = item.loyaltyPropertyRateUpgrade
  return {
    ...unclaimed,
    sellTotal: fallbackPricing.sellTotal,
    ...(fallbackPricing.luxPlusTotal != null && { luxPlusTotal: fallbackPricing.luxPlusTotal }),
    ...(upgrade && { loyaltyPropertyRateUpgrade: withoutMarkedLoyaltyUpgradePricing(upgrade) }),
  } as T
}
